/* Sendinvo analytics tracker — standalone, no dependencies.
 *
 * Fires a single beacon on initial page load and on every SPA route change
 * (the main app dispatches `popstate` after pushState, so we listen on it).
 *
 * Design constraints:
 *   - Must never block rendering — loaded with `defer`, uses sendBeacon().
 *   - No cookies, and nothing written to the browser unless someone asks
 *     not to be counted — the server fingerprints the visitor daily from
 *     ip+ua. Anonymous, privacy-respecting.
 *   - Skip in dev/localhost, when Do Not Track or Global Privacy Control is
 *     set, and once someone has pressed "Stop counting my visits".
 *   - Don't log `/admin` or `/api` paths — we don't want our own
 *     dashboard views polluting the marketing numbers.
 *
 * Why a button of our own. Without consent, this counting rests on PECR's
 * exception for statistics about how a service is used (Schedule A1,
 * paragraph 5, since 5 February 2026), and that exception holds only while
 * the visitor is told what it's for and has "a simple means of objecting,
 * free of charge". The ICO's guidance says not to rely solely on browser
 * settings for that, so DNT and GPC alone weren't enough. The privacy
 * policy's section 6 carries the button (`data-sendinvo-dont-count`); the
 * flag it stores (OPT_OUT_KEY) is the record of that objection, which is the
 * one thing kept here, and it is kept only because the person asked.
 */
(function () {
  "use strict";

  var OPT_OUT_KEY = "sendinvo.dontCount";
  var STOPPED_TEXT = "This browser’s visits aren’t counted.";

  function optedOut() {
    try { return window.localStorage.getItem(OPT_OUT_KEY) === "1"; } catch { return false; }
  }

  // Say so on any button already showing, and on one pressed now. Works on
  // the static /privacy page and on the policy the app draws, because the
  // listener is on the document rather than on the button.
  function markStopped(button) {
    button.textContent = STOPPED_TEXT;
    button.disabled = true;
  }
  function markAll() {
    try {
      var buttons = document.querySelectorAll("[data-sendinvo-dont-count]");
      for (var i = 0; i < buttons.length; i++) markStopped(buttons[i]);
    } catch {}
  }
  document.addEventListener("click", function (evt) {
    var target = evt.target && evt.target.closest ? evt.target.closest("[data-sendinvo-dont-count]") : null;
    if (!target) return;
    evt.preventDefault();
    try { window.localStorage.setItem(OPT_OUT_KEY, "1"); } catch {}
    markStopped(target);
  });

  if (optedOut()) {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", markAll);
    else markAll();
    window.addEventListener("popstate", function () { setTimeout(markAll, 0); });
    return;
  }

  try {
    // Honour Do Not Track and Global Privacy Control where the browser
    // exposes them.
    if (navigator.doNotTrack === "1" || window.doNotTrack === "1" || navigator.msDoNotTrack === "1" || navigator.globalPrivacyControl === true) {
      return;
    }
  } catch {}

  // Detect environment (matches src/js/app.js convention).
  var host = location.hostname;
  var IS_LOCAL = host === "localhost" || host === "127.0.0.1";
  var IS_SENDINVO = host === "sendinvo.com" || (host && host.slice(-12) === ".sendinvo.com");
  if (IS_LOCAL) return; // Don't send beacons during development.

  var API = IS_SENDINVO ? "https://api.sendinvo.com" : "https://ar-ap-api-frankfurt.onrender.com";

  // Every SPA route passed through here in the last second is deduped —
  // guards against an over-eager popstate dispatcher firing twice.
  var lastPath = null;
  var lastAt = 0;

  function send(pathOverride) {
    try {
      // Pressed "Stop counting my visits" on this page, after it loaded.
      if (optedOut()) return;
      var path = pathOverride || (location.pathname + (location.search || ""));
      // Skip admin / API / internal paths.
      if (/^\/(?:admin|api|internal|webhooks)(?:\/|$)/.test(location.pathname)) return;

      // De-dupe identical paths fired within 500ms.
      var now = Date.now();
      if (path === lastPath && now - lastAt < 500) return;
      lastPath = path;
      lastAt = now;

      // Pull UTM params from the original URL.
      var q = new URLSearchParams(location.search || "");
      var payload = {
        path: location.pathname,
        referrer: document.referrer || null,
        surface: /\/app(?:\/|$)|\bapp\./.test(location.pathname) || host.indexOf("app.") === 0 ? "app" : "marketing",
        utm_source: q.get("utm_source") || null,
        utm_medium: q.get("utm_medium") || null,
        utm_campaign: q.get("utm_campaign") || null,
      };

      var body = JSON.stringify(payload);
      // Prefer sendBeacon — fire-and-forget, works during page unload.
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon(API + "/api/track", blob)) return;
      }
      // Fallback to fetch. Intentionally not awaited.
      fetch(API + "/api/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
        keepalive: true,
        credentials: "omit",
        mode: "cors"
      }).catch(function () {});
    } catch {}
  }

  // Fire once on load.
  if (document.readyState === "complete" || document.readyState === "interactive") {
    send();
  } else {
    document.addEventListener("DOMContentLoaded", function () { send(); });
  }

  // Fire on SPA navigation.
  window.addEventListener("popstate", function () { send(); });

  // Also hook pushState / replaceState so we catch navigations even if
  // the app forgets to dispatch popstate. This is a gentle monkey-patch.
  ["pushState", "replaceState"].forEach(function (fn) {
    var orig = history[fn];
    if (typeof orig !== "function") return;
    history[fn] = function () {
      var r = orig.apply(this, arguments);
      // Let the synchronous listener run first, then beacon.
      setTimeout(send, 0);
      return r;
    };
  });
})();
